<template>
  <div class="tasks-breadcrumb" ref="container">
    <div v-if="isTask" class="bc-back-button" @click="goBack()">
      <img src="/img/icons/back_arrow.png" />
    </div>

    <!-- Off-screen div: measures natural widths of all items -->
    <div class="bc-measure" ref="measure" aria-hidden="true">
      <span
        v-for="(item, index) in breadcrumbItems"
        :key="index"
        class="bc-measure-entry"
      >
        <span v-if="index > 0" class="bc-sep">&nbsp;&gt;</span
        ><span class="bc-item" data-measure-item>{{ item.label }}</span>
      </span>
    </div>

    <div class="breadcrumb-full">
      <!-- Left anchor: always visible -->
      <span class="bc-left-anchor">
        <span
          v-if="leftAnchorItem"
          :class="[
            'bc-item',
            {
              'bc-current': leftAnchorItem.isCurrent,
              'bc-link':
                leftAnchorItem.isClickable || leftAnchorItem.isProductList,
              'bc-product-list': leftAnchorItem.isProductList,
            },
          ]"
          @click="leftAnchorItem.onClick && leftAnchorItem.onClick()"
          >{{ leftAnchorItem.label }}</span
        >
      </span>

      <!-- Remaining entries: dots + visible middle + current -->
      <span v-for="(entry, i) in renderItems" :key="i" class="bc-entry">
        <span class="bc-sep">&nbsp;&gt;</span>

        <b-dropdown
          v-if="entry.type === 'dots'"
          position="is-bottom-right"
          :close-on-click="true"
          :mobile-modal="false"
          :append-to-body="true"
        >
          <template #trigger>
            <div class="bc-dots">
              <b-icon icon="dots-horizontal" />
            </div>
          </template>
          <b-dropdown-item custom>
            <div
              v-for="(item, di) in hiddenItems"
              :key="di"
              :class="[
                'bc-dropdown-path',
                {
                  'bc-dropdown-path--link':
                    item.isClickable || item.isProductList,
                },
              ]"
              @click="item.onClick && item.onClick()"
            >
              {{ item.label }}
            </div>
          </b-dropdown-item>
        </b-dropdown>

        <span
          v-else-if="entry.type === 'middle'"
          class="bc-item bc-link"
          @click="entry.item.onClick && entry.item.onClick()"
          >{{ entry.item.label }}</span
        >

        <span v-else class="bc-item bc-current">{{ entry.item.label }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import FoldersManagerMixin from '../../mixins/FoldersManagerMixin';
import {
  GET_CURRENT_TASK,
  GET_CURRENT_FOLDER,
} from '../../store/tasks/getters/getterTypes';

export default {
  name: 'tasks-breadcrumb',
  mixins: [FoldersManagerMixin],
  props: {
    breadcrumbs: {
      type: Array,
      default: null,
    },
    isTask: {
      type: Boolean,
      default: false,
    },
    tool: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      oilUser: '',
      visibleMiddleStart: 1,
    };
  },
  computed: {
    task() {
      if (this.isTask) {
        return this.$store.getters[GET_CURRENT_TASK] || {};
      }
      return this.$store.state.task;
    },
    folderName() {
      if (this.isTask) {
        const currentFolder = this.$store.getters[GET_CURRENT_FOLDER];
        if (!currentFolder || !currentFolder.taskName) return '';
        if (currentFolder.folderId) {
          return (this.folder && this.folder.taskName) || '';
        }
        return currentFolder.taskName;
      }
      return (this.folder && this.folder.taskName) || '';
    },
    subfolderName() {
      if (this.isTask) {
        const currentFolder = this.$store.getters[GET_CURRENT_FOLDER];
        if (currentFolder && currentFolder.folderId) {
          return currentFolder.taskName || '';
        }
        return '';
      }
      return (this.subfolder && this.subfolder.taskName) || '';
    },
    user() {
      return this.$store.state.currentUser;
    },
    currentJob() {
      return this.$store.state.JobsModule.currentJob;
    },
    isExternalUser() {
      return !!(
        this.$route.params.token ||
        (this.user && this.user._id === this.oilUser)
      );
    },
    productListText() {
      return this.$route.name && this.$route.name.includes('editor')
        ? this.$t('product_template_list')
        : this.$t('work_order_list');
    },
    modelFormatted() {
      const m = this.currentJob?.model;
      if (!m) return '';
      if (Array.isArray(m)) return m.map(({ text }) => text).join(' / ');
      return m;
    },
    jobLabel() {
      const job = this.currentJob;
      if (!job) return '';
      if (this.tool.includes('editor')) {
        return [job.title, this.modelFormatted, job.documentNumber]
          .filter(Boolean)
          .join(' / ');
      }
      return [job.title, job.customerInfoText, job.projectInfoText]
        .filter(Boolean)
        .join(' / ');
    },
    // Items prepended to prop-based breadcrumbs: work list + job
    prefixItems() {
      const items = [];
      if (!this.isExternalUser) {
        items.push({
          label: this.productListText,
          isProductList: true,
          isCurrent: false,
          isClickable: false,
          onClick: this.gotoProductList,
        });
      }
      if (this.jobLabel) {
        const { id } = this.$route.params;
        const { token } = this.$route.query || {};
        items.push({
          label: this.jobLabel,
          tooltip: this.jobLabel,
          isCurrent: false,
          isClickable: true,
          onClick: () =>
            this.$router.push({
              name: `${this.tool}-tasks`,
              params: { id },
              query: token ? { token } : undefined,
            }),
        });
      }
      return items;
    },
    // Full items when breadcrumbs prop is null: old store-based logic
    storeBasedItems() {
      const items = [];
      if (!this.isExternalUser) {
        items.push({
          label: this.productListText,
          isProductList: true,
          onClick: this.gotoProductList,
        });
      }
      if (this.jobLabel) {
        items.push({
          label: this.jobLabel,
          tooltip: this.jobLabel,
          isClickable: !!(this.folderName || this.isTask),
          onClick: () =>
            this.task && this.task.folderId
              ? this.gotoTaskList(true)
              : this.gotoTaskList(),
        });
      }
      if (this.folderName) {
        items.push({
          label: this.folderName,
          isClickable: !!this.folderName,
          onClick: () => this.gotoTaskList(false, true),
        });
      }
      if (this.subfolderName) {
        const currentFolder = this.$store.getters[GET_CURRENT_FOLDER];
        const parentFolderId = currentFolder && currentFolder.folderId;
        items.push({
          label: this.subfolderName,
          isClickable: true,
          onClick: () => {
            const { id } = this.$route.params;
            const { token: tok } = this.$route.query || {};
            if (parentFolderId && this.task && this.task.folderId) {
              this.$router.push({
                path: `/view/${this.tool}/${id}/folder/${parentFolderId}/${this.task.folderId}`,
                query: tok ? { token: tok } : undefined,
              });
            } else {
              this.gotoTaskList();
            }
          },
        });
      }
      if (this.isTask && this.task && this.task.taskName) {
        items.push({ label: this.task.taskName, isCurrent: true });
      } else if (items.length > 0) {
        items[items.length - 1] = {
          ...items[items.length - 1],
          isCurrent: true,
        };
      }
      return items;
    },
    breadcrumbItems() {
      if (this.breadcrumbs !== null) {
        const n = this.breadcrumbs.length;
        const propItems = this.breadcrumbs.map((crumb, index) => {
          const isCurrent = index === n - 1;
          const tooltip = crumb.subLabels
            ? crumb.subLabels.filter(Boolean).join(' / ')
            : null;
          return {
            label: crumb.label,
            tooltip,
            isCurrent,
            isProductList: false,
            isClickable: !isCurrent && !!crumb.to,
            onClick:
              !isCurrent && crumb.to ? () => this.$router.push(crumb.to) : null,
          };
        });
        return [...this.prefixItems, ...propItems];
      }
      return this.storeBasedItems;
    },
    leftAnchorItem() {
      return this.breadcrumbItems[0] || null;
    },
    currentItem() {
      const n = this.breadcrumbItems.length;
      return this.breadcrumbItems[n - 1] || null;
    },
    hiddenItems() {
      return this.breadcrumbItems.slice(1, this.visibleMiddleStart);
    },
    visibleMiddleItems() {
      const n = this.breadcrumbItems.length;
      return this.breadcrumbItems.slice(this.visibleMiddleStart, n - 1);
    },
    renderItems() {
      const entries = [];
      if (this.hiddenItems.length) {
        entries.push({ type: 'dots' });
      }
      this.visibleMiddleItems.forEach((item) => {
        entries.push({ type: 'middle', item });
      });
      if (this.breadcrumbItems.length > 1 && this.currentItem) {
        entries.push({ type: 'current', item: this.currentItem });
      }
      return entries;
    },
  },
  watch: {
    breadcrumbItems() {
      this.$nextTick(() => this.calculateVisibility());
    },
  },
  async mounted() {
    this.oilUser = process.env.VUE_APP_OIL_USER_EMAIL;

    this.$store.watch(
      (state, getters) => getters[GET_CURRENT_FOLDER],
      async (currentFolder) => {
        if (!this.isTask) return;
        if (currentFolder && currentFolder.folderId) {
          this.folder = await this.$store.dispatch('getTask', {
            id: currentFolder.folderId,
          });
        } else {
          this.folder = null;
        }
      },
      { immediate: true }
    );

    this._resizeObserver = new ResizeObserver(() => this.calculateVisibility());
    this._resizeObserver.observe(this.$refs.container);
  },
  beforeDestroy() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },
  methods: {
    goBack() {
      if (this.breadcrumbs !== null) {
        const n = this.breadcrumbs.length;
        const target = n >= 2 ? this.breadcrumbs[n - 2] : this.breadcrumbs[0];
        if (target && target.to) {
          this.$router.push(target.to);
        }
      } else {
        this.gotoTaskList();
      }
    },
    calculateVisibility() {
      const { container } = this.$refs;
      const measureEl = this.$refs.measure;
      if (!container || !measureEl) return;

      const backButtonWidth = this.isTask ? 50 : 0;
      const availableWidth = container.offsetWidth - backButtonWidth - 20;

      const measureItems = Array.from(
        measureEl.querySelectorAll('[data-measure-item]')
      );
      const itemWidths = measureItems.map((el) => el.offsetWidth);
      const sepEl = measureEl.querySelector('.bc-sep');
      const sepWidth = sepEl ? sepEl.offsetWidth : 25;
      const dotsWidth = 50;

      const n = itemWidths.length;
      if (n <= 2) {
        this.visibleMiddleStart = 1;
        return;
      }

      for (let k = 1; k < n; k++) {
        let w = itemWidths[0];
        if (k > 1) w += sepWidth + dotsWidth;
        for (let i = k; i < n - 1; i++) {
          w += sepWidth + itemWidths[i];
        }
        w += sepWidth + itemWidths[n - 1];
        if (w <= availableWidth) {
          this.visibleMiddleStart = k;
          return;
        }
      }
      this.visibleMiddleStart = n - 1;
    },
    gotoProductList() {
      if (this.tool.includes('editor')) {
        this.$router.push({ name: 'editor-list' });
      } else if (this.tool.includes('inspector')) {
        this.$router.push({ name: 'inspector-list' });
      } else if (this.tool.includes('publisher')) {
        this.$router.push({ name: 'publisher-list' });
      } else {
        this.$router.push({ name: 'report-list' });
      }
    },
    gotoTaskList(task = false, folder = false) {
      if (!this.folderName && !this.isTask) return false;

      const { id } = this.$route.params;
      const { token } = this.$route.query || {};

      if (folder) {
        const currentFolder = this.$store.getters[GET_CURRENT_FOLDER];
        const targetFolderId =
          (currentFolder && currentFolder.folderId) ||
          this.folderId ||
          (this.task && this.task.folderId);
        if (targetFolderId) {
          const path = `/view/${this.tool}/${id}/folder/${targetFolderId}`;
          return this.$router.push({
            path,
            query: token ? { token } : undefined,
          });
        }
      }

      if (!task && this.task && this.task.folderId) {
        const currentFolder = this.$store.getters[GET_CURRENT_FOLDER];
        if (currentFolder && currentFolder.folderId) {
          const path = `/view/${this.tool}/${id}/folder/${currentFolder.folderId}/${this.task.folderId}`;
          return this.$router.push({
            path,
            query: token ? { token } : undefined,
          });
        }
        const path = `/view/${this.tool}/${id}/folder/${this.task.folderId}`;
        return this.$router.push({
          path,
          query: token ? { token } : undefined,
        });
      }

      if (this.subfolderId) {
        const path = `/view/${this.tool}/${id}/folder/${this.folderId}`;
        return this.$router.push({
          path,
          query: token ? { token } : undefined,
        });
      }

      this.$store.commit('SET_TASK_OBJECT', {});
      this.$router.push({
        name: `${this.tool}-tasks`,
        params: { id },
        query: token ? { token } : undefined,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tasks-breadcrumb {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
  position: relative;
}

.bc-back-button {
  display: inline-flex;
  width: 50px;
  height: 100%;
  cursor: pointer;
  flex-shrink: 0;
  img {
    width: 25px;
    margin: auto;
    &:hover {
      filter: brightness(0%);
    }
  }
}

.bc-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  align-items: center;
  min-width: max-content;
}

.bc-measure-entry {
  display: inline-flex;
  align-items: center;
}

.breadcrumb-full {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
  flex: 1 1 auto;
  padding-right: 20px;
}

.bc-left-anchor {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.bc-entry {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.bc-sep {
  margin-right: 5px;
  flex-shrink: 0;
}

.bc-item {
  font-size: 18px;
  overflow: hidden;
  white-space: nowrap;
  max-height: 50px;
  margin: auto 0;
  color: #999;
}

.bc-current {
  font-weight: bold;
  color: #333;
}

.bc-link {
  cursor: pointer;
  &:hover {
    color: #555;
  }
}

.bc-product-list {
  min-width: 90px;
  flex-shrink: 0;
}

.bc-dots {
  cursor: pointer;
  margin-top: 15px;
  margin-right: 17px;
  flex-shrink: 0;
  &:hover {
    color: gray;
  }
}

::v-deep .dropdown-item.is-custom {
  padding: 0 !important;
  min-width: max-content;
}

.bc-dropdown-path {
  display: block;
  padding: 12px 16px;
  font-size: 18px;
  white-space: nowrap;
  line-height: 1.4;
  color: #555;
  border-bottom: 1px solid #eee;
  cursor: default;

  &:hover {
    background-color: #dce8f5;
  }

  &:last-child {
    border-bottom: none;
  }
}

.bc-dropdown-path--link {
  cursor: pointer;
}
</style>
